import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ReportFilters = ({ onApplyFilters, onResetFilters }) => {
  const [filters, setFilters] = useState({
    dateRange: 'last30days',
    startDate: '',
    endDate: '',
    hotel: 'all',
    reportType: 'occupancy',
    format: 'pdf'
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const dateRangeOptions = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: 'last7days', label: 'Últimos 7 dias' },
    { value: 'last30days', label: 'Últimos 30 dias' },
    { value: 'thisMonth', label: 'Este mês' },
    { value: 'lastMonth', label: 'Mês passado' },
    { value: 'thisYear', label: 'Este ano' },
    { value: 'custom', label: 'Período personalizado' }
  ];

  const hotelOptions = [
    { value: '', label: 'Todos os Hotéis' }
    // Os hotéis serão carregados dinamicamente da API
  ];

  const reportTypeOptions = [
    { value: 'occupancy', label: 'Ocupação' },
    { value: 'financial', label: 'Financeiro' },
    { value: 'guest', label: 'Satisfação do Hóspede' },
    { value: 'operational', label: 'Performance Operacional' },
    { value: 'revenue', label: 'Análise de Receita' },
    { value: 'booking', label: 'Fontes de Reserva' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'csv', label: 'CSV' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
  };

  const handleReset = () => {
    const resetFilters = {
      dateRange: 'last30days',
      startDate: '',
      endDate: '',
      hotel: 'all',
      reportType: 'occupancy',
      format: 'pdf'
    };
    setFilters(resetFilters);
    onResetFilters();
  };

  return (
    <div className="group relative bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity"></div>

      <div className="relative">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Icon name="Filter" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                Filtros de Relatório
              </h3>
              <p className="text-sm text-muted-foreground">
                Personalize os dados dos seus relatórios
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            iconName={showAdvanced ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="self-start sm:self-auto"
          >
            <span className="hidden sm:inline">{showAdvanced ? 'Ocultar' : 'Avançado'}</span>
            <span className="sm:hidden">{showAdvanced ? 'Menos' : 'Mais'}</span>
          </Button>
        </div>

        {/* Main Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Período
            </label>
            <Select
              options={dateRangeOptions}
              value={filters.dateRange}
              onChange={(value) => handleFilterChange('dateRange', value)}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Hotel
            </label>
            <Select
              options={hotelOptions}
              value={filters.hotel}
              onChange={(value) => handleFilterChange('hotel', value)}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Tipo de Relatório
            </label>
            <Select
              options={reportTypeOptions}
              value={filters.reportType}
              onChange={(value) => handleFilterChange('reportType', value)}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Formato
            </label>
            <Select
              options={formatOptions}
              value={filters.format}
              onChange={(value) => handleFilterChange('format', value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Custom Date Range */}
        {filters.dateRange === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Data Inicial
              </label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Data Final
              </label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="border-t border-border pt-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Settings" size={18} className="text-primary" />
              <h4 className="text-sm font-semibold text-foreground">Filtros Avançados</h4>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Segmento de Mercado
                </label>
                <Select
                  options={[
                    { value: 'all', label: 'Todos' },
                    { value: 'business', label: 'Corporativo' },
                    { value: 'leisure', label: 'Lazer' },
                    { value: 'group', label: 'Grupos' }
                  ]}
                  value="all"
                  onChange={() => {}}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Canal de Reserva
                </label>
                <Select
                  options={[
                    { value: 'all', label: 'Todos' },
                    { value: 'direct', label: 'Direto' },
                    { value: 'ota', label: 'OTAs' },
                    { value: 'gds', label: 'GDS' }
                  ]}
                  value="all"
                  onChange={() => {}}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Tipo de Quarto
                </label>
                <Select
                  options={[
                    { value: 'all', label: 'Todos' },
                    { value: 'standard', label: 'Standard' },
                    { value: 'deluxe', label: 'Deluxe' },
                    { value: 'suite', label: 'Suíte' }
                  ]}
                  value="all"
                  onChange={() => {}}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-border">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>Os relatórios são atualizados em tempo real</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={handleReset}
              iconName="RotateCcw"
              iconPosition="left"
              size="sm"
            >
              <span className="hidden sm:inline">Limpar</span>
              <span className="sm:hidden">Reset</span>
            </Button>
            <Button
              variant="default"
              onClick={handleApply}
              iconName="Search"
              iconPosition="left"
              size="sm"
              className="bg-primary hover:bg-primary/90"
            >
              <span className="hidden sm:inline">Aplicar Filtros</span>
              <span className="sm:hidden">Aplicar</span>
            </Button>
          </div>
        </div>

        {/* Hover effect overlay */}
        <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl pointer-events-none"></div>
      </div>
    </div>
  );
};

export default ReportFilters;